import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button,
  Alert, IconButton, InputAdornment, CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// ─── Geometric background pattern (SVG inline) ───────────────
const GridPattern = () => (
  <svg
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.07 }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
        <path d="M 48 0 L 0 0 0 48" fill="none" stroke="white" strokeWidth="0.8" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#grid)" />
  </svg>
);

const FloatingOrb = ({ size, top, left, delay, color }) => (
  <div style={{
    position: 'absolute',
    width: size, height: size,
    top, left,
    borderRadius: '50%',
    background: color,
    filter: 'blur(60px)',
    opacity: 0.35,
    animation: `float ${3 + delay}s ease-in-out ${delay}s infinite`,
    pointerEvents: 'none',
  }} />
);

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Une erreur est survenue lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      background: 'var(--bg-primary)',
    }}>

      {/* ── Left panel ─────────────────────────────────────── */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        width: '48%',
        position: 'relative',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: 6,
        background: 'linear-gradient(145deg, #0c1120 0%, #1a2540 50%, #0f1e40 100%)',
        overflow: 'hidden',
      }}>
        <GridPattern />
        <FloatingOrb size="320px" top="-80px" left="-80px" delay={0}   color="rgba(59,91,219,0.6)" />
        <FloatingOrb size="240px" top="50%"  left="60%"  delay={1.5}  color="rgba(245,158,11,0.4)" />
        <FloatingOrb size="180px" top="75%"  left="10%"  delay={0.8}  color="rgba(6,182,212,0.3)" />

        {/* Logo */}
        <Box sx={{ position: 'relative', zIndex: 1 }} className="animate-fade-up">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{
              width: 40, height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b5bdb, #5c7cfa)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(59,91,219,0.5)',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M3 9L12 3L21 9V21H15V15H9V21H3V9Z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </Box>
            <Typography sx={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 800,
              fontSize: '1.4rem',
              color: '#fff',
              letterSpacing: '-0.03em',
            }}>
              EduWorks
            </Typography>
          </Box>
          <Typography variant="caption" sx={{
            fontFamily: "'JetBrains Mono', monospace",
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontSize: '0.68rem',
          }}>
            Gestion des travaux scolaires
          </Typography>
        </Box>

        {/* Central headline */}
        <Box sx={{ position: 'relative', zIndex: 1 }} className="animate-fade-up delay-2">
          <Typography sx={{
            fontFamily: "'Syne', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.04em',
            color: '#fff',
            mb: 2,
          }}>
            Pilotez vos<br />
            <Box component="span" sx={{
              background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              interventions
            </Box>
            <br />avec précision.
          </Typography>
          <Typography sx={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: '0.95rem',
            lineHeight: 1.7,
            maxWidth: 360,
            fontWeight: 400,
          }}>
            Centralise les demandes, planifie les interventions et
            suit l'avancement en temps réel — pour tous les services.
          </Typography>
        </Box>

        {/* Stats row */}
        <Box sx={{
          position: 'relative', zIndex: 1,
          display: 'flex', gap: 3,
        }} className="animate-fade-up delay-3">
          {[
            { label: 'Demandes traitées', value: '2 400+' },
            { label: 'Établissements', value: '12' },
            { label: 'Taux de résolution', value: '94%' },
          ].map(({ label, value }) => (
            <Box key={label}>
              <Typography sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#fff',
                letterSpacing: '-0.03em',
              }}>
                {value}
              </Typography>
              <Typography sx={{
                fontSize: '0.72rem',
                color: 'rgba(255,255,255,0.35)',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Right panel (form) ──────────────────────────────── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, sm: 6 },
        background: 'var(--bg-surface)',
      }}>
        <Box sx={{ width: '100%', maxWidth: 420 }}>

          {/* Mobile logo */}
          <Box sx={{ display: { md: 'none' }, mb: 4, textAlign: 'center' }} className="animate-fade-up">
            <Typography sx={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 800,
              fontSize: '2rem',
              letterSpacing: '-0.04em',
              background: 'linear-gradient(135deg, #3b5bdb, #f59e0b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              EduWorks
            </Typography>
          </Box>

          {/* Heading */}
          <Box sx={{ mb: 4 }} className="animate-fade-up">
            <Typography variant="overline" sx={{ color: '#3b5bdb', mb: 1, display: 'block' }}>
              Bienvenue
            </Typography>
            <Typography sx={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              fontSize: '2rem',
              letterSpacing: '-0.03em',
              lineHeight: 1.15,
              color: 'var(--text-primary)',
              mb: 0.5,
            }}>
              Connexion
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
              Accédez à votre espace de gestion
            </Typography>
          </Box>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
          >
            {error && (
              <Alert severity="error" className="animate-scale-in" sx={{ borderRadius: '12px' }}>
                {error}
              </Alert>
            )}

            <Box className="animate-fade-up delay-1">
              <Typography variant="caption" sx={{
                fontWeight: 600,
                color: 'var(--text-secondary)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                mb: 0.75,
                display: 'block',
              }}>
                Adresse email
              </Typography>
              <TextField
                required
                fullWidth
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="vous@exemple.fr"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.95rem' } }}
              />
            </Box>

            <Box className="animate-fade-up delay-2">
              <Typography variant="caption" sx={{
                fontWeight: 600,
                color: 'var(--text-secondary)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                mb: 0.75,
                display: 'block',
              }}>
                Mot de passe
              </Typography>
              <TextField
                required
                fullWidth
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(v => !v)}
                        edge="end"
                        size="small"
                        sx={{ color: 'var(--text-muted)' }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.95rem' } }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              className="animate-fade-up delay-3"
              sx={{
                mt: 1, py: 1.5,
                fontSize: '1rem',
                fontWeight: 700,
                fontFamily: "'Syne', sans-serif",
                letterSpacing: '-0.01em',
                borderRadius: '12px',
              }}
            >
              {loading
                ? <CircularProgress size={20} sx={{ color: 'white' }} />
                : 'Se connecter'
              }
            </Button>
          </Box>

          {/* Footer */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 5,
              color: 'var(--text-muted)',
              letterSpacing: '0.01em',
            }}
            className="animate-fade-up delay-4"
          >
            EduWorks © {new Date().getFullYear()} — Softmarket
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
